import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DownOutlined,
  EditOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  ExportOutlined,
  LinkOutlined,
  PaperClipOutlined,
  PlusOutlined,
  SendOutlined,
  UploadOutlined,
  UpOutlined,
} from '@ant-design/icons';
import { Button, Input, Modal, Select, Tabs, Tag, Tooltip } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import BaseLoader from 'components/atoms/base-loader';
import BaseButton from 'components/atoms/baseButton';
import OutlinedLink from 'components/atoms/icons/outlined-link';
import OutlinedPaperClipBG from 'components/atoms/icons/outlined-paper-clip-bg';
import OutlinedPill from 'components/atoms/icons/outlined-pill';
import { DateFormats } from 'infrastructure/enums';
import { DateFormatter, prepareDoctorName } from 'infrastructure/functions';
import { useDoctorInfo } from 'infrastructure/hooks/doctor/use-doctor-info';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import {
  memo,
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import EmptyBox from 'components/atoms/icons/empty-box';
import { Routes } from 'router/routes';
import { showResult } from 'infrastructure/hooks/utils';

import { PDFEditor } from '../../../oneplatform/pdf-editor';
import { getStatusLabel, mapLetterContent } from '../../hooks/helper';
import {
  downloadPapCaseAttachment,
  useCreatePapCaseExternalLink,
  useDeletePapCaseAttachment,
  useDeletePapCaseExternalLink,
  useGetLetterTemplates,
  useGetPapCaseDetail,
  useGetPapCaseExternalLinks,
  useGetPapFormMedications,
  useSavePapCaseAttachment,
  useSavePapCaseMedicationForm,
  useUpdatePapCase,
  useUpdatePapCaseLetter,
  useUpdatePapCaseNurseNote,
  useUpdateStatusPapCase,
  useUpdateStatusWithReviewPapCase,
} from '../../hooks/use-pap-cases';
import CaseReviewModal from './case-review-modal';
import PapCaseControllers from './pap-case-controllers';
import s from './pap-case-details-modal.module.scss';

import type { Patient } from 'infrastructure/classes/patient/patient';
import type {
  Attachment,
  IPapFormMedication,
  PapCaseStatus,
} from '../../types';

const PAP_CASE_MODAL_Z_INDEX = 1020;
const PAP_CASE_STACKED_MODAL_Z_INDEX = 1120;
const PAP_CASE_MODAL_BASE_WIDTH = 1280;
const MAX_ATTACHMENTS = 10;
const MAX_EXTERNAL_LINKS = 10;
const MAX_DISPLAY_REVIEW_LENGTH = 100;

const isValidHttpUrl = (value: string) => {
  if (!value || value.length > 2048) return false;

  try {
    const { protocol, hostname } = new URL(value);

    if (protocol !== 'http:' && protocol !== 'https:') return false;
    if (!hostname) return false;

    const normalizedHost = hostname.toLowerCase();
    const isLocalhost = normalizedHost === 'localhost';
    const isIpv4 = /^(?:\d{1,3}\.){3}\d{1,3}$/.test(normalizedHost);
    const isDomain = /^[a-z0-9-]+(?:\.[a-z0-9-]+)+$/i.test(normalizedHost);
    const isIpv6 = normalizedHost.includes(':');

    return isLocalhost || isIpv4 || isIpv6 || isDomain;
  } catch {
    return false;
  }
};

interface PapCaseDetailsModalProps {
  open: boolean;
  patientGuid: string;
  papCaseGuid: string;
  patientInfo?: Patient;
  onDownloadPapForm?: (
    papFormGuid: string,
    papCaseGuid?: string,
  ) => Promise<string>;
  onClose: () => void;
  isReviewerView?: boolean;
  isReadOnly?: boolean;
}

interface PapPdfPanelProps {
  medicationKey: string;
  selectedPapFormMedication?: IPapFormMedication;
  pdfUrl: string;
  noPdfMessage?: string;
  onRegisterExportHandler?: (
    exportHandler: (() => Promise<Uint8Array>) | null,
  ) => void;
  readOnly: boolean;
  showBanner?: boolean;
  fieldData: Record<string, string | boolean | string[]>;
  onFormDataChange: (
    nextData: Map<string, string | boolean | string[]>,
  ) => void;
}

const PapPdfPanel = memo(
  ({
    medicationKey,
    selectedPapFormMedication,
    pdfUrl,
    noPdfMessage,
    onRegisterExportHandler,
    readOnly,
    showBanner = true,
    fieldData,
    onFormDataChange,
  }: PapPdfPanelProps) => {
    const { t } = useTranslation();

    if (!medicationKey) {
      return (
        <div className={s['pap-form-viewer']}>
          <div className={s['pap-form-preview']}>
            <PDFEditor
              pdfUrl=""
              mode="pap-form"
              readOnly
              showExportButton={false}
              height={360}
              className={s['pap-form-editor']}
              onFormDataChange={onFormDataChange}
              emptyContent={
                <div className={s['pap-form-empty-content']}>
                  <EmptyBox />
                  <p className={s['pap-form-empty-title']}>
                    {t('papCase.noMedicationSelected')}
                  </p>
                  <p className={s['pap-form-empty-text']}>
                    {t('papCase.selectMedicationToViewForm')}
                  </p>
                </div>
              }
            />
          </div>
        </div>
      );
    }

    return (
      <div className={s['pap-form-viewer']}>
        {showBanner && (
          <div className={s['pap-form-banner']}>
            <OutlinedPill />
            <span className={s['pap-form-banner-text']}>
              {`${selectedPapFormMedication?.name}${
                selectedPapFormMedication?.activePapForm?.note
                  ? ` - ${selectedPapFormMedication.activePapForm.note}`
                  : ''
              }`}
            </span>
          </div>
        )}
        <div className={s['pap-form-preview']}>
          {pdfUrl ? (
            <PDFEditor
              pdfUrl={pdfUrl}
              onRegisterExportHandler={onRegisterExportHandler}
              mode="pap-form"
              readOnly={readOnly}
              showExportButton={false}
              height={360}
              className={s['pap-form-editor']}
              initialFormData={fieldData}
              onFormDataChange={onFormDataChange}
            />
          ) : (
            <div className={s['pap-form-no-pdf']}>
              {noPdfMessage || t('papCase.pdfTemplateUnavailable')}
            </div>
          )}
        </div>
      </div>
    );
  },
  (prev, next) =>
    prev.medicationKey === next.medicationKey &&
    prev.pdfUrl === next.pdfUrl &&
    prev.readOnly === next.readOnly &&
    prev.fieldData === next.fieldData &&
    prev.noPdfMessage === next.noPdfMessage &&
    prev.showBanner === next.showBanner &&
    prev.selectedPapFormMedication === next.selectedPapFormMedication,
);

const PapCaseDetailsModal: FC<PapCaseDetailsModalProps> = ({
  open,
  patientGuid,
  papCaseGuid,
  patientInfo,
  onDownloadPapForm,
  onClose,
  isReviewerView = false,
  isReadOnly,
}) => {
  const { t } = useTranslation();
  const { mutateAsync: updatePapCaseLetter, isLoading: isUpdatingLetter } =
    useUpdatePapCaseLetter(patientGuid, () => {
      setIsEditingLetter(false);
    });

  const { mutateAsync: updatePapCase, isLoading: isUpdatingCase } =
    useUpdatePapCase(patientGuid, () => {
      setIsEditingCaseDetail(false);
    });

  const {
    mutateAsync: updatePapCaseNurseNote,
    isLoading: isUpdatingNurseNote,
  } = useUpdatePapCaseNurseNote(patientGuid, () => {
    setIsEditingNurseNote(false);
  });

  const {
    mutateAsync: createPapCaseExternalLink,
    isLoading: isCreatingExternalLink,
  } = useCreatePapCaseExternalLink(patientGuid, () => {
    setIsAddingExternalLink(false);
    setDraftExternalResourceTitle('');
    setDraftExternalResourceUrl('');
    setDraftExternalResourceDescription('');
  });

  const {
    mutateAsync: deletePapCaseExternalLink,
    isLoading: isDeletingExternalLink,
  } = useDeletePapCaseExternalLink(patientGuid);

  const {
    mutateAsync: deletePapCaseAttachment,
    isLoading: isDeletingPapCaseAttachment,
  } = useDeletePapCaseAttachment(patientGuid);

  const {
    mutateAsync: savePapCaseMedicationForm,
    isLoading: isSavingPapCaseMedicationForm,
  } = useSavePapCaseMedicationForm(patientGuid);

  const {
    mutateAsync: savePapCaseAttachment,
    isLoading: isSavingPapCaseAttachment,
  } = useSavePapCaseAttachment(patientGuid, () => {
    setIsUploadModalOpen(false);
  });

  const { data: currentCase, isLoading: isLoadingDetail } =
    useGetPapCaseDetail(papCaseGuid);

  const { patientInfo: fetchedPatientInfo } = usePatientInfo({ patientGuid });
  const resolvedPatientInfo = patientInfo ?? fetchedPatientInfo;

  const { doctorInfo, doctorInfoLoading } = useDoctorInfo({
    doctorGuid: resolvedPatientInfo?.doctorGuid,
  });
  const providerName = doctorInfo
    ? prepareDoctorName({
        title: doctorInfo.title,
        firstName: doctorInfo.firstName,
        lastName: doctorInfo.lastName,
      })
    : '';

  const { data: letterTemplates, isLoading: isLoadingLetterTemplates } =
    useGetLetterTemplates();

  const hiddenUploadInputRef = useRef<HTMLInputElement>(null);
  const previousOpenRef = useRef(false);
  const previousCaseGuidRef = useRef<string | null>(null);
  const loadedViewPapFormGuidRef = useRef('');

  const [activeTab, setActiveTab] = useState('letter');
  const [isEditingLetter, setIsEditingLetter] = useState(false);
  const [isEditingCaseDetail, setIsEditingCaseDetail] = useState(false);
  const [isEditingNurseNote, setIsEditingNurseNote] = useState(false);
  const [isAddingExternalLink, setIsAddingExternalLink] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(
    null,
  );

  const [draftNurseNote, setDraftNurseNote] = useState('');

  const [draftExternalResourceTitle, setDraftExternalResourceTitle] =
    useState('');
  const [draftExternalResourceUrl, setDraftExternalResourceUrl] = useState('');
  const [
    draftExternalResourceDescription,
    setDraftExternalResourceDescription,
  ] = useState('');

  const [draftTemplateKey, setDraftTemplateKey] = useState('');
  const [draftLetterContent, setDraftLetterContent] = useState('');
  const [isEditingPapForm, setIsEditingPapForm] = useState(false);
  const [isChangingMedication, setIsChangingMedication] = useState(false);
  const [draftPapFormMedicationKey, setDraftPapFormMedicationKey] =
    useState('');
  const [draftPapFormPdfUrl, setDraftPapFormPdfUrl] = useState('');
  const draftPapFormPdfUrlRef = useRef('');
  const papFormPdfLoadTokenRef = useRef(0);
  const createdObjectUrlsRef = useRef<string[]>([]);
  const [draftPapFormFieldData, setDraftPapFormFieldData] = useState<
    Record<string, string | boolean | string[]>
  >({});
  const draftPapFormFieldDataRef = useRef<
    Record<string, string | boolean | string[]>
  >({});
  // Cache saved field data by case GUID so it survives modal close/reopen.
  // PDF annotation round-tripping can lose field values for some field types
  // (radios, dropdowns, checkboxes), so we restore from this cache instead.
  const savedFieldDataCacheRef = useRef<
    Record<string, Record<string, string | boolean | string[]>>
  >({});
  const [isPapFormPdfLoading, setIsPapFormPdfLoading] = useState(false);

  const papFormExportHandlerRef = useRef<(() => Promise<Uint8Array>) | null>(
    null,
  );
  const [draftCaseName, setDraftCaseName] = useState('');
  const [draftSpecialist, setDraftSpecialist] = useState<boolean>(false);

  const [showFullNote, setShowFullNote] = useState(false);

  const [openingCaseStatus, setOpeningCaseStatus] =
    useState<PapCaseStatus | null>(null);

  const [editingWarning, setEditingWarning] = useState<{
    open: boolean;
    message: string;
  }>({ open: false, message: '' });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const statusAlertMap: Record<
    Exclude<PapCaseStatus, 'draft'>,
    string
  > = useMemo(
    () => ({
      approved: t('papCase.papApprovedDesc'),
      'in-review': t('papCase.papInReviewDesc'),
      'needs-info': t('papCase.papNeedInfoDesc'),
      submitted: t('papCase.papSubmittedDesc'),
      denied: t('papCase.papDeniedDesc'),
    }),
    [t],
  );

  const statusBGColorMap: Record<
    Exclude<PapCaseStatus, 'draft'>,
    string
  > = useMemo(
    () => ({
      approved: '#F6FFED',
      'in-review': '#F5E8F9',
      'needs-info': '#FFFBE6',
      submitted: '#F0FAFF',
      denied: '#FFF2F0',
    }),
    [],
  );

  const statusBorderColorMap: Record<
    Exclude<PapCaseStatus, 'draft'>,
    string
  > = useMemo(
    () => ({
      approved: '#B7EB8F',
      'in-review': '#F5E8F9',
      'needs-info': '#FFE58F',
      submitted: '#ADDDFF',
      denied: '#FFCCC7',
    }),
    [],
  );

  const statusIconMap: Record<
    Exclude<PapCaseStatus, 'draft'>,
    ReactNode
  > = useMemo(
    () => ({
      approved: <CheckCircleOutlined className={s['icon-approved']} />,
      'in-review': <ClockCircleOutlined className={s['icon-in-review']} />,
      'needs-info': (
        <ExclamationCircleOutlined className={s['icon-needs-info']} />
      ),
      submitted: <SendOutlined className={s['icon-submitted']} />,
      denied: <CloseCircleOutlined className={s['icon-denied']} />,
    }),
    [],
  );

  const defaultTemplateGuid = '';

  const { data: papFormMedications, isLoading: isLoadingPapFormMedications } =
    useGetPapFormMedications();

  const {
    data: papCaseExternalLinks,
    isLoading: isLoadingExternalLinks,
    isFetching: isFetchingExternalLinks,
  } = useGetPapCaseExternalLinks(open ? currentCase?.guid : undefined);

  const queryClient = useQueryClient();

  const { mutateAsync: updateCaseStatus, isLoading: isUpdatingStatus } =
    useUpdateStatusPapCase(papCaseGuid, patientGuid, () => {
      setOpeningCaseStatus(null);
      queryClient.invalidateQueries({ queryKey: ['papWorkplaceManagement'] });
      queryClient.invalidateQueries({
        queryKey: ['papWorkplaceManagementOverview'],
      });
      onClose();
    });

  const {
    mutateAsync: updateCaseStatusWithReview,
    isLoading: isUpdatingStatusWithReview,
  } = useUpdateStatusWithReviewPapCase(papCaseGuid, patientGuid, () => {
    setOpeningCaseStatus(null);
    queryClient.invalidateQueries({ queryKey: ['papWorkplaceManagement'] });
    queryClient.invalidateQueries({
      queryKey: ['papWorkplaceManagementOverview'],
    });
    onClose();
  });

  const selectedPapFormMedication = papFormMedications?.find(
    (item) =>
      item.guid === draftPapFormMedicationKey ||
      item.guid === currentCase?.medicationGuid,
  );

  useEffect(() => {
    if (!open || !currentCase) return;

    const isFirstOpen = !previousOpenRef.current && open;
    const isDifferentCase =
      previousCaseGuidRef.current !== null &&
      previousCaseGuidRef.current !== currentCase.guid;

    if (!isFirstOpen && !isDifferentCase) {
      return;
    }

    setActiveTab('letter');

    const caseStatus = currentCase.status ?? '';
    setIsEditingLetter(
      !currentCase.content &&
        ['draft', 'needs-info', 'in-review'].includes(caseStatus),
    );
    setIsEditingPapForm(
      !currentCase.medicationGuid &&
        ['draft', 'needs-info'].includes(caseStatus),
    );
    setIsChangingMedication(false);
    setIsEditingCaseDetail(false);
    setIsEditingNurseNote(false);
    setIsAddingExternalLink(false);
    setIsUploadModalOpen(false);
    setSelectedUploadFile(null);
    setDraftNurseNote(currentCase.note || '');
    setDraftExternalResourceTitle('');
    setDraftExternalResourceUrl('');
    setDraftExternalResourceDescription('');
    setDraftCaseName(currentCase.name);
    setDraftSpecialist(currentCase.isSpecialist);
    setDraftTemplateKey(
      currentCase.letterTemplateCategories || defaultTemplateGuid,
    );
    setDraftLetterContent(currentCase.content ?? '');
    setDraftPapFormMedicationKey(currentCase.medicationGuid || '');
    setDraftPapFormPdfUrl('');
    const cachedFieldData =
      savedFieldDataCacheRef.current[currentCase.guid] || {};
    setDraftPapFormFieldData(cachedFieldData);
    draftPapFormFieldDataRef.current = { ...cachedFieldData };
    papFormExportHandlerRef.current = null;
    loadedViewPapFormGuidRef.current = '';

    const initialPapFormGuid = currentCase.latestForm?.guid ?? '';
    const initialPapFormPdfUrl = currentCase.latestForm?.file?.fileUrl ?? '';

    if (initialPapFormGuid) {
      // Mark as loaded immediately so the view-mode effect (below) does
      // not start a redundant second download for the same form GUID.
      loadedViewPapFormGuidRef.current = initialPapFormGuid;
      loadPapFormPdfForPapFormGuid(initialPapFormGuid).catch(() => {
        loadedViewPapFormGuidRef.current = '';
      });
    } else if (
      initialPapFormPdfUrl &&
      !initialPapFormPdfUrl.startsWith('s3://')
    ) {
      loadPapFormPdfFromCaseSource(initialPapFormPdfUrl).catch(() => undefined);
    }

    previousOpenRef.current = open;
    previousCaseGuidRef.current = currentCase.guid;
  }, [defaultTemplateGuid, open, currentCase]);

  useEffect(() => {
    if (!open) {
      previousOpenRef.current = false;
      loadedViewPapFormGuidRef.current = '';
    }
  }, [open]);

  useEffect(() => {
    if (!open || !currentCase) return;
    if (activeTab !== 'papForm' || isEditingPapForm) return;

    const latestPapFormGuid = currentCase.latestForm?.guid ?? '';

    if (!latestPapFormGuid || !onDownloadPapForm) return;

    if (
      loadedViewPapFormGuidRef.current === latestPapFormGuid &&
      draftPapFormPdfUrl
    ) {
      return;
    }

    loadedViewPapFormGuidRef.current = latestPapFormGuid;
    loadPapFormPdfForPapFormGuid(latestPapFormGuid).catch(() => {
      loadedViewPapFormGuidRef.current = '';
    });
  }, [
    activeTab,
    draftPapFormPdfUrl,
    isEditingPapForm,
    onDownloadPapForm,
    open,
    currentCase,
  ]);

  useEffect(
    () => () => {
      createdObjectUrlsRef.current.forEach((objectUrl) => {
        URL.revokeObjectURL(objectUrl);
      });
      createdObjectUrlsRef.current = [];
    },
    [],
  );

  useEffect(() => {
    draftPapFormPdfUrlRef.current = draftPapFormPdfUrl;
  }, [draftPapFormPdfUrl]);

  const onPapFormDataChange = useCallback(
    (nextData: Map<string, string | boolean | string[]>) => {
      draftPapFormFieldDataRef.current = Object.fromEntries(nextData);
    },
    [],
  );

  const onRegisterPapFormExportHandler = useCallback(
    (exportHandler: (() => Promise<Uint8Array>) | null) => {
      papFormExportHandlerRef.current = exportHandler;
    },
    [],
  );

  const waitForPapFormEditorReady = useCallback(async () => {
    const maxAttempts = 40;

    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (draftPapFormPdfUrlRef.current && papFormExportHandlerRef.current) {
        await new Promise<void>((resolve) => {
          requestAnimationFrame(() => resolve());
        });
        return;
      }

      await new Promise<void>((resolve) => {
        setTimeout(resolve, 50);
      });
    }

    throw new Error('PAP form PDF editor is not ready');
  }, []);

  const handleEditLetter = () => {
    setDraftTemplateKey(
      currentCase?.letterTemplateCategories || defaultTemplateGuid,
    );
    setDraftLetterContent(currentCase?.content ?? '');
    setIsEditingLetter(true);
  };

  const onCancelLetter = () => {
    setDraftTemplateKey(
      currentCase?.letterTemplateCategories || defaultTemplateGuid,
    );
    setDraftLetterContent(currentCase?.content ?? '');
    setIsEditingLetter(!currentCase?.content);
  };

  const onSaveLetter = async () => {
    const nextTemplateGuid = draftTemplateKey || defaultTemplateGuid;
    if (!nextTemplateGuid) return;

    await updatePapCaseLetter({
      papCaseGuid: currentCase?.guid || '',
      letterTemplateGuid: nextTemplateGuid,
      content: draftLetterContent,
    });
  };

  const createLocalPdfUrl = async (sourceUrl: string) => {
    if (!sourceUrl) {
      throw new Error('Invalid PAP form PDF URL');
    }

    const response = await fetch(sourceUrl);

    if (!response.ok) {
      throw new Error('Failed to download PAP form PDF');
    }

    const pdfBlob = await response.blob();
    const objectUrl = URL.createObjectURL(pdfBlob);
    createdObjectUrlsRef.current.push(objectUrl);
    return objectUrl;
  };

  const loadPapFormPdfFromCaseSource = async (sourceUrl: string) => {
    if (!sourceUrl) {
      setDraftPapFormPdfUrl('');
      papFormExportHandlerRef.current = null;
      return '';
    }

    const currentToken = papFormPdfLoadTokenRef.current + 1;
    papFormPdfLoadTokenRef.current = currentToken;

    try {
      setIsPapFormPdfLoading(true);
      const localPdfUrl = await createLocalPdfUrl(sourceUrl);

      if (papFormPdfLoadTokenRef.current !== currentToken) {
        return '';
      }

      setDraftPapFormPdfUrl(localPdfUrl);
      return localPdfUrl;
    } finally {
      if (papFormPdfLoadTokenRef.current === currentToken) {
        setIsPapFormPdfLoading(false);
      }
    }
  };

  const loadPapFormPdfForMedication = async (medicationKey: string) => {
    const selectedMedication = papFormMedications?.find(
      (item) => item.guid === medicationKey,
    );

    if (selectedMedication && !selectedMedication.hasActivePapForm) {
      setDraftPapFormPdfUrl('');
      papFormExportHandlerRef.current = null;
      return '';
    }

    if (!selectedMedication?.papFormGuid || !onDownloadPapForm) {
      setDraftPapFormPdfUrl('');
      papFormExportHandlerRef.current = null;
      return '';
    }

    const currentToken = papFormPdfLoadTokenRef.current + 1;
    papFormPdfLoadTokenRef.current = currentToken;

    try {
      setIsPapFormPdfLoading(true);
      const downloadUrl = await onDownloadPapForm(
        selectedMedication.papFormGuid,
      );

      if (!downloadUrl) {
        setDraftPapFormPdfUrl('');
        return '';
      }

      const localPdfUrl = await createLocalPdfUrl(downloadUrl);

      if (papFormPdfLoadTokenRef.current !== currentToken) {
        return '';
      }

      setDraftPapFormPdfUrl(localPdfUrl);
      return localPdfUrl;
    } finally {
      if (papFormPdfLoadTokenRef.current === currentToken) {
        setIsPapFormPdfLoading(false);
      }
    }
  };

  const loadPapFormPdfForPapFormGuid = async (papFormGuid: string) => {
    if (!papFormGuid || !onDownloadPapForm) {
      return '';
    }

    const currentToken = papFormPdfLoadTokenRef.current + 1;
    papFormPdfLoadTokenRef.current = currentToken;

    try {
      setIsPapFormPdfLoading(true);
      const downloadUrl = await onDownloadPapForm(
        papFormGuid,
        currentCase?.guid,
      );

      if (!downloadUrl) {
        setDraftPapFormPdfUrl('');
        return '';
      }

      const localPdfUrl = await createLocalPdfUrl(downloadUrl);

      if (papFormPdfLoadTokenRef.current !== currentToken) {
        return '';
      }

      setDraftPapFormPdfUrl(localPdfUrl);
      return localPdfUrl;
    } finally {
      if (papFormPdfLoadTokenRef.current === currentToken) {
        setIsPapFormPdfLoading(false);
      }
    }
  };

  const handleEditMedication = async () => {
    const medicationKey = currentCase?.medicationGuid || '';

    setDraftPapFormMedicationKey(medicationKey);
    const currentFieldData = { ...draftPapFormFieldDataRef.current };
    setDraftPapFormFieldData(currentFieldData);
    setIsEditingPapForm(true);
    setIsChangingMedication(!medicationKey);

    if (draftPapFormPdfUrlRef.current) {
      return;
    }

    if (currentCase?.latestForm?.guid) {
      await loadPapFormPdfForPapFormGuid(currentCase?.latestForm.guid);
      return;
    }

    if (
      currentCase?.latestForm?.file?.fileUrl &&
      !currentCase?.latestForm.file.fileUrl.startsWith('s3://')
    ) {
      await loadPapFormPdfFromCaseSource(currentCase?.latestForm.file.fileUrl);
      return;
    }

    if (medicationKey) {
      await loadPapFormPdfForMedication(medicationKey);
    }
  };

  const handleDownloadPapForm = async () => {
    try {
      const latestForm = currentCase?.latestForm;
      if (!latestForm || !currentCase) return;

      if (latestForm.guid && onDownloadPapForm) {
        const downloadUrl = await onDownloadPapForm(
          latestForm.guid,
          currentCase.guid,
        );
        if (downloadUrl) {
          const fileName = `PAP_Form_${currentCase.name}_${
            latestForm.version ?? 1
          }.pdf`;
          const anchor = document.createElement('a');
          anchor.href = downloadUrl;
          anchor.download = fileName;
          anchor.rel = 'noopener noreferrer';
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
        }
        return;
      }

      const fileUrl = latestForm.file?.fileUrl;
      if (fileUrl && !fileUrl.startsWith('s3://')) {
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      showResult('Failed to download PAP Form. Please try again.');
    }
  };

  const onCancelPapForm = () => {
    const originalMedicationKey = currentCase?.medicationGuid || '';
    const medicationChanged =
      draftPapFormMedicationKey !== originalMedicationKey;

    setDraftPapFormMedicationKey(originalMedicationKey);
    setIsEditingPapForm(!originalMedicationKey);
    setIsChangingMedication(false);

    if (medicationChanged) {
      setDraftPapFormPdfUrl('');
      setDraftPapFormFieldData({});
      draftPapFormFieldDataRef.current = {};
      setIsPapFormPdfLoading(false);
      papFormExportHandlerRef.current = null;
      loadedViewPapFormGuidRef.current = '';

      if (currentCase?.latestForm?.guid) {
        loadPapFormPdfForPapFormGuid(currentCase?.latestForm.guid).catch(
          () => undefined,
        );
        return;
      }

      if (
        currentCase?.latestForm?.file?.fileUrl &&
        !currentCase?.latestForm.file.fileUrl.startsWith('s3://')
      ) {
        loadPapFormPdfFromCaseSource(
          currentCase?.latestForm.file.fileUrl,
        ).catch(() => undefined);
      }
    } else {
      const restoredFieldData = { ...draftPapFormFieldDataRef.current };
      setDraftPapFormFieldData(restoredFieldData);
      draftPapFormFieldDataRef.current = restoredFieldData;
    }
  };

  const handleSaveMedicationForm = async () => {
    try {
      const hasPreviousSavedForm =
        !!currentCase?.medicationGuid &&
        !!currentCase?.latestForm?.file?.fileUrl;
      const currentVersion = Number.parseFloat(
        String(currentCase?.latestForm?.version ?? '0'),
      );
      const nextVersion = !hasPreviousSavedForm
        ? '1.0'
        : `${(Number.isFinite(currentVersion) && currentVersion > 0
            ? currentVersion + 1
            : 2
          ).toFixed(1)}`;

      let savedFormGuid = currentCase?.latestForm?.guid ?? '';
      const selectedMedication = papFormMedications?.find(
        (item) => item.guid === draftPapFormMedicationKey,
      );

      if (draftPapFormMedicationKey && selectedMedication?.papFormGuid) {
        const ensuredPdfUrl =
          draftPapFormPdfUrl ||
          (await loadPapFormPdfForMedication(draftPapFormMedicationKey));

        if (!ensuredPdfUrl) {
          throw new Error('PAP form PDF is not available for export');
        }

        await waitForPapFormEditorReady();

        if (!papFormExportHandlerRef.current) {
          throw new Error('PAP form export handler is not ready');
        }

        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const timeoutPromise = new Promise<never>((_resolve, reject) => {
          timeoutId = setTimeout(
            () => reject(new Error('Failed to export edited PAP form PDF')),
            15_000,
          );
        });
        const exportedPdfBytes = await Promise.race([
          papFormExportHandlerRef.current().finally(() => {
            clearTimeout(timeoutId);
          }),
          timeoutPromise,
        ]);

        if (!exportedPdfBytes || exportedPdfBytes.byteLength === 0) {
          throw new Error('Failed to export edited PAP form PDF');
        }

        const exportedPdfByteArray = Uint8Array.from(exportedPdfBytes);
        const exportedPdfArrayBuffer = new ArrayBuffer(
          exportedPdfByteArray.byteLength,
        );
        new Uint8Array(exportedPdfArrayBuffer).set(exportedPdfByteArray);

        const exportedPdfFile = new File(
          [exportedPdfArrayBuffer],
          `pap-case-${currentCase?.guid}-v${nextVersion}.pdf`,
          {
            type: 'application/pdf',
          },
        );

        const saveResult = await savePapCaseMedicationForm({
          papCaseGuid: currentCase?.guid ?? '',
          medicationGuid: draftPapFormMedicationKey,
          medicationPapFormGuid: selectedMedication.papFormGuid,
          file: exportedPdfFile,
        });

        savedFormGuid = saveResult.formGuid || savedFormGuid;

        if (savedFormGuid) {
          setDraftPapFormFieldData({ ...draftPapFormFieldDataRef.current });
          savedFieldDataCacheRef.current[currentCase?.guid ?? ''] = {
            ...draftPapFormFieldDataRef.current,
          };
        }

        // Use the exported PDF bytes directly as the viewer source so the
        // viewer shows the EXACT PDF that was saved — without depending on
        // a backend re-download that might return stale data (the mutation's
        // onSuccess query invalidation runs async after mutateAsync resolves).
        const savedPdfBlob = new Blob([exportedPdfArrayBuffer], {
          type: 'application/pdf',
        });
        const savedPdfUrl = URL.createObjectURL(savedPdfBlob);
        createdObjectUrlsRef.current.push(savedPdfUrl);
        setDraftPapFormPdfUrl(savedPdfUrl);
      }
      // Mark the current form as loaded so the view-mode effect does not
      // re-download with a potentially stale currentCase.latestForm.guid.
      loadedViewPapFormGuidRef.current =
        savedFormGuid || currentCase?.latestForm?.guid || '';
    } finally {
      setIsEditingPapForm(false);
    }
  };

  const handleEditNurseNote = () => {
    setDraftNurseNote(currentCase?.note || '');
    setIsEditingNurseNote(true);
  };

  const handleCancelNurseNote = () => {
    setDraftNurseNote(currentCase?.note || '');
    setIsEditingNurseNote(false);
  };

  const handleSaveNurseNote = async () => {
    await updatePapCaseNurseNote({
      papCaseGuid: currentCase?.guid ?? '',
      note: draftNurseNote,
    });
  };

  const handleOpenUploadModal = () => {
    setSelectedUploadFile(null);
    setIsUploadModalOpen(true);
  };

  const handleUploadAttachment = () => {
    if (!selectedUploadFile) return;

    savePapCaseAttachment({
      papCaseGuid: currentCase?.guid ?? '',
      file: selectedUploadFile,
    });
  };

  const handleDeleteAttachment = (attachmentId: string) => {
    deletePapCaseAttachment({
      papCaseGuid: currentCase?.guid ?? '',
      attachmentGuid: attachmentId,
    });
  };

  const handleDownloadAttachment = async (attachment: Attachment) => {
    try {
      const downloadUrl = await downloadPapCaseAttachment(
        currentCase?.guid ?? '',
        attachment.guid,
      );
      if (downloadUrl) {
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch {
      showResult('Failed to download attachment. Please try again.');
    }
  };

  const handleAddExternalLink = async () => {
    createPapCaseExternalLink({
      papCaseGuid: currentCase?.guid ?? '',
      title: draftExternalResourceTitle.trim(),
      url: draftExternalResourceUrl.trim(),
      description: draftExternalResourceDescription.trim(),
    });
  };

  const handleDeleteExternalLink = async (resourceId: string) => {
    await deletePapCaseExternalLink({
      papCaseGuid: currentCase?.guid ?? '',
      linkGuid: resourceId,
    });
  };

  const onCancelCaseDetail = () => {
    setDraftCaseName(currentCase?.name ?? '');
    setDraftSpecialist(currentCase?.isSpecialist ?? false);
    setIsEditingCaseDetail(false);
  };

  const handleUpdateCaseDetail = async () => {
    const nextName = draftCaseName.trim();
    await updatePapCase({
      papCaseGuid: currentCase?.guid ?? '',
      name: nextName,
      isSpecialist: draftSpecialist,
    });
  };

  const isModalApiSaving =
    isUpdatingLetter ||
    isSavingPapCaseMedicationForm ||
    isUpdatingCase ||
    isUpdatingNurseNote ||
    isCreatingExternalLink ||
    isDeletingExternalLink ||
    isSavingPapCaseAttachment ||
    isDeletingPapCaseAttachment ||
    isUpdatingStatus ||
    isUpdatingStatusWithReview;

  const isModalLoading = isLoadingDetail || isModalApiSaving;

  const isAnySectionEditing =
    isEditingLetter ||
    isEditingPapForm ||
    isEditingCaseDetail ||
    isEditingNurseNote ||
    isAddingExternalLink;

  const isLetterDirty =
    draftLetterContent !== (currentCase?.content ?? '') ||
    draftTemplateKey !==
      (currentCase?.letterTemplateCategories || defaultTemplateGuid);

  const showEditingWarning = (actionLabel: string) => {
    setEditingWarning({
      open: true,
      message: t('papCase.finishEditingMessage', { actionLabel }),
    });
  };

  const onTabChange = (nextTab: string) => {
    const hasUnsavedLetterChanges =
      isEditingLetter && Boolean(draftLetterContent.trim());
    const hasUnsavedPapFormChanges =
      isEditingPapForm && Boolean(draftPapFormMedicationKey);
    const isBlockingTabSwitch =
      hasUnsavedLetterChanges ||
      hasUnsavedPapFormChanges ||
      isEditingCaseDetail ||
      isEditingNurseNote ||
      isAddingExternalLink;

    if (isBlockingTabSwitch) {
      showEditingWarning(t('papCase.switchingTabs'));
      return;
    }
    setActiveTab(nextTab);
  };

  const handleStatusTransaction = (
    currentStatus: PapCaseStatus,
    nextStatus: PapCaseStatus,
    reviewNote?: string,
  ) => {
    if (isAnySectionEditing) {
      return showEditingWarning(t('papCase.markingCaseReadyForReview'));
    }

    if (currentStatus === 'draft' || currentStatus === 'needs-info') {
      updateCaseStatus({
        nextStatus,
      });
    } else {
      updateCaseStatusWithReview({
        nextStatus,
        reviewNote: reviewNote || '',
      });
    }
  };

  const hasUnsavedChanges =
    (!isReviewerView && isEditingLetter) ||
    isEditingPapForm ||
    isEditingCaseDetail ||
    isEditingNurseNote ||
    isAddingExternalLink ||
    isUploadModalOpen;

  const onRequestClose = () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }

    setDiscardConfirmOpen(true);
  };

  const patientName = resolvedPatientInfo?.loginInfo?.fullName || '-';
  const patientDob = resolvedPatientInfo?.loginInfo?.birthDate
    ? DateFormatter({
        date: resolvedPatientInfo.loginInfo.birthDate,
        format: DateFormats['MM/dd/yyyy'],
      })
    : '-';

  const letterTemplateValues: Record<string, string> = {
    PatientName: patientName || '-',
    ProviderName: providerName || '-',
  };

  const papFormContent: ReactNode = (
    <PapPdfPanel
      medicationKey={draftPapFormMedicationKey}
      selectedPapFormMedication={selectedPapFormMedication}
      pdfUrl={draftPapFormPdfUrl}
      noPdfMessage={
        draftPapFormMedicationKey &&
        selectedPapFormMedication &&
        !selectedPapFormMedication.hasActivePapForm
          ? t('papCase.noPapFormAvailable')
          : t('papCase.pdfTemplateUnavailable')
      }
      onRegisterExportHandler={onRegisterPapFormExportHandler}
      readOnly={!isEditingPapForm}
      showBanner={!isEditingPapForm}
      fieldData={draftPapFormFieldData}
      onFormDataChange={onPapFormDataChange}
    />
  );
  const hasLetterContent = Boolean((currentCase?.content || '').trim());
  const externalLinkTitle = draftExternalResourceTitle.trim();
  const externalLinkUrl = draftExternalResourceUrl.trim();
  const isExternalLinkUrlValid = isValidHttpUrl(externalLinkUrl);

  let externalResourcesContent: ReactNode = (
    <div className={s['additional-empty']}>
      <span className={s['additional-empty-icon']}>
        <LinkOutlined />
      </span>
      <p className={s['additional-empty-text']}>
        {t('papCase.noExternalResourcesAdded')}
      </p>
    </div>
  );

  const isEditableLetter = ['draft', 'needs-info', 'in-review'].includes(
    currentCase?.status ?? '',
  );
  const isEditableMedication = ['draft', 'needs-info'].includes(
    currentCase?.status ?? '',
  );
  const isEditableNurseNote = ['draft', 'needs-info', 'in-review'].includes(
    currentCase?.status ?? '',
  );
  const isEditableAttachments = ['draft', 'needs-info', 'in-review'].includes(
    currentCase?.status ?? '',
  );
  const isEditableLinks = ['draft', 'needs-info', 'in-review'].includes(
    currentCase?.status ?? '',
  );

  const isViewOnly =
    isReviewerView ||
    ['submitted', 'approved', 'denied'].includes(currentCase?.status ?? '') ||
    isReadOnly;

  const attachments = currentCase?.attachments || [];
  const externalLinks = papCaseExternalLinks || [];

  const isLongReview =
    (currentCase?.latestReview?.note?.length || 0) > MAX_DISPLAY_REVIEW_LENGTH;

  if (isLoadingExternalLinks) {
    externalResourcesContent = <BaseLoader loading />;
  } else if (externalLinks?.length) {
    externalResourcesContent = (
      <div className={s['additional-list']}>
        {externalLinks?.map((resource) => (
          <div key={resource.id} className={s['additional-item']}>
            <div className={s['additional-item-content']}>
              <span>
                <OutlinedLink />
              </span>
              <div className={s['additional-item-text']}>
                <p
                  title={resource.title}
                  className={s['additional-item-title']}
                >
                  {resource.title}
                </p>
                <a
                  title={resource.url}
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={s['additional-item-url']}
                >
                  {resource.url}
                </a>
                <p
                  className={s['additional-item-description']}
                  title={resource?.description || ''}
                >
                  {resource.description}
                </p>
              </div>
            </div>
            {!isViewOnly && (
              <Button
                type="text"
                className={s['additional-remove-button']}
                onClick={() => handleDeleteExternalLink(resource.id)}
                loading={isFetchingExternalLinks || isDeletingExternalLink}
                icon={<DeleteOutlined />}
                disabled={
                  !isEditableLinks ||
                  isFetchingExternalLinks ||
                  isDeletingExternalLink
                }
              />
            )}
          </div>
        ))}
      </div>
    );
  } else if (isViewOnly) {
    externalResourcesContent = (
      <p className={s['additional-ro-empty']}>{t('papCase.noResource')}</p>
    );
  }

  return (
    <Modal
      open={open}
      title={
        <span className={s['modal-title']}>
          {t('papCase.modalTitle', { caseName: currentCase?.name })}
        </span>
      }
      onCancel={onRequestClose}
      footer={null}
      destroyOnClose
      centered
      width={PAP_CASE_MODAL_BASE_WIDTH}
      zIndex={PAP_CASE_MODAL_Z_INDEX}
      loading={isLoadingDetail}
      wrapClassName={s['pap-details-modal']}
    >
      <div className={s['modal-body-wrapper']}>
        {currentCase?.status !== 'draft' && !isReviewerView && (
          <div
            className={s['modal-sub-header']}
            style={{
              backgroundColor:
                statusBGColorMap[
                  currentCase?.status as Exclude<PapCaseStatus, 'draft'>
                ],
              border: `1px solid ${
                statusBorderColorMap[
                  currentCase?.status as Exclude<PapCaseStatus, 'draft'>
                ]
              }`,
            }}
          >
            {
              statusIconMap[
                currentCase?.status as Exclude<PapCaseStatus, 'draft'>
              ]
            }
            <p>
              {
                statusAlertMap[
                  currentCase?.status as Exclude<PapCaseStatus, 'draft'>
                ]
              }
            </p>
          </div>
        )}
        <div
          className={s['modal-main-content']}
          style={isModalLoading ? { pointerEvents: 'none' } : undefined}
        >
          <div className={s.content}>
            <div className={s.left}>
              <Tabs
                activeKey={activeTab}
                onChange={onTabChange}
                destroyInactiveTabPane={false}
                items={[
                  {
                    key: 'letter',
                    label: t('papCase.tabLetter'),
                    children: (
                      <div>
                        <div className={s['letter-header']}>
                          <div>
                            <p className={s['letter-title']}>
                              {isViewOnly
                                ? t('papCase.letterContent')
                                : t('papCase.selectLetterTemplate')}
                            </p>
                            {!isViewOnly && (
                              <p className={s['letter-helper']}>
                                {t('papCase.choosePapLetterByProviderType')}
                              </p>
                            )}
                          </div>
                          <div className={s['letter-actions']}>
                            {!isViewOnly && !isEditingLetter && (
                              <BaseButton
                                type="secondary"
                                label={t('papCase.edit')}
                                icon={<EditOutlined />}
                                onClick={handleEditLetter}
                                disabled={!isEditableLetter}
                              />
                            )}
                            {!isViewOnly && isEditingLetter && (
                              <>
                                {(Boolean(currentCase?.content) ||
                                  isLetterDirty) && (
                                  <BaseButton
                                    type="secondary"
                                    label={t('controls.cancel')}
                                    onClick={onCancelLetter}
                                  />
                                )}
                                <BaseButton
                                  label={t('papCase.save')}
                                  onClick={onSaveLetter}
                                  loading={isUpdatingLetter}
                                  disabled={
                                    (!draftTemplateKey &&
                                      !defaultTemplateGuid) ||
                                    isModalApiSaving
                                  }
                                />
                              </>
                            )}
                          </div>
                        </div>

                        {!isViewOnly && (
                          <Select
                            className={s['letter-select']}
                            value={draftTemplateKey || undefined}
                            placeholder={t('papCase.selectTemplatePlaceholder')}
                            disabled={!isEditingLetter || doctorInfoLoading}
                            options={letterTemplates?.map((template) => ({
                              label: template.name,
                              value: template.guid,
                            }))}
                            onChange={(value) => {
                              setDraftTemplateKey(value);
                              const template = letterTemplates?.find(
                                (item) => item.guid === value,
                              );
                              setDraftLetterContent(
                                template?.content
                                  ? mapLetterContent(
                                      template.content,
                                      letterTemplateValues,
                                    )
                                  : '',
                              );
                            }}
                            loading={
                              isLoadingLetterTemplates || doctorInfoLoading
                            }
                          />
                        )}

                        {isEditingLetter ? (
                          <Input.TextArea
                            className={s['letter-input']}
                            value={draftLetterContent}
                            onChange={(e) =>
                              setDraftLetterContent(e.target.value)
                            }
                            placeholder={t('papCase.letterContentPlaceholder')}
                            disabled={isViewOnly}
                          />
                        ) : null}

                        {!isEditingLetter && hasLetterContent && (
                          <div className={s['letter-content']}>
                            {currentCase?.content}
                          </div>
                        )}

                        {!isEditingLetter && !hasLetterContent && (
                          <div className={s['pap-form-empty']}>
                            <EmptyBox />
                            <p className={s['pap-form-empty-title']}>
                              {t('papCase.noLetterSelected')}
                            </p>
                            <p className={s['pap-form-empty-text']}>
                              {t('papCase.selectLetterToView')}
                            </p>
                          </div>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'papForm',
                    label: t('papCase.tabPapForm'),
                    forceRender: true,
                    children: (
                      <div className={s['tab-pane']}>
                        {isEditingPapForm ? (
                          <>
                            <div className={s['letter-header']}>
                              <div>
                                <p className={s['letter-title']}>
                                  {t('papCase.selectMedication')}
                                </p>
                                <p className={s['letter-helper']}>
                                  {draftPapFormMedicationKey
                                    ? t('papCase.choosePapLetterByProviderType')
                                    : t(
                                        'papCase.selectMedicationPapAssistance',
                                      )}
                                </p>
                              </div>
                              <div className={s['letter-actions']}>
                                {(currentCase?.medicationGuid ||
                                  draftPapFormMedicationKey) && (
                                  <BaseButton
                                    type="secondary"
                                    label={t('controls.cancel')}
                                    onClick={onCancelPapForm}
                                  />
                                )}
                                <BaseButton
                                  label={t('papCase.save')}
                                  onClick={handleSaveMedicationForm}
                                  loading={isSavingPapCaseMedicationForm}
                                  disabled={
                                    !draftPapFormMedicationKey ||
                                    isPapFormPdfLoading ||
                                    isModalApiSaving
                                  }
                                />
                              </div>
                            </div>

                            {!isChangingMedication &&
                            draftPapFormMedicationKey ? (
                              <div className={s['pap-form-edit-banner']}>
                                <div className={s['pap-form-edit-banner-info']}>
                                  <OutlinedPill />
                                  <span className={s['pap-form-banner-text']}>
                                    {selectedPapFormMedication?.name
                                      ? `${selectedPapFormMedication.name}${
                                          selectedPapFormMedication
                                            .activePapForm?.note
                                            ? ` - ${selectedPapFormMedication.activePapForm.note}`
                                            : ''
                                        }`
                                      : t('papCase.medicationFallback')}
                                  </span>
                                </div>
                                <Button
                                  type="link"
                                  size="small"
                                  className={s['pap-form-change-btn']}
                                  onClick={() => setIsChangingMedication(true)}
                                >
                                  {t('papCase.changeMedication')}
                                </Button>
                              </div>
                            ) : (
                              <Select
                                className={s['letter-select']}
                                showSearch
                                optionFilterProp="title"
                                value={draftPapFormMedicationKey || undefined}
                                placeholder={t(
                                  'papCase.selectMedicationPlaceholder',
                                )}
                                loading={isLoadingPapFormMedications}
                                options={papFormMedications?.map((item) => ({
                                  value: item.guid,
                                  title: item.name,
                                  disabled: !item.hasActivePapForm,
                                  label: (
                                    <span className={s['medication-option']}>
                                      <span
                                        className={`${
                                          s['medication-option-dot']
                                        } ${
                                          item.hasActivePapForm
                                            ? s['medication-option-dot-green']
                                            : s['medication-option-dot-gray']
                                        }`}
                                      />
                                      <span
                                        className={
                                          item.hasActivePapForm
                                            ? s['medication-option-text']
                                            : s['medication-option-text-gray']
                                        }
                                      >
                                        {item.name}
                                      </span>
                                    </span>
                                  ),
                                }))}
                                onChange={async (value) => {
                                  setDraftPapFormMedicationKey(value);
                                  setDraftPapFormPdfUrl('');
                                  await loadPapFormPdfForMedication(value);
                                  setDraftPapFormFieldData({});
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <div className={s['letter-header']}>
                            <p className={s['letter-title']}>
                              {t('papCase.tabPapForm')}
                            </p>
                            <div className={s['letter-actions']}>
                              {isReviewerView &&
                                currentCase?.latestForm &&
                                (currentCase.latestForm.guid ||
                                  (currentCase.latestForm.file?.fileUrl &&
                                    !currentCase.latestForm.file.fileUrl.startsWith(
                                      's3://',
                                    ))) && (
                                  <BaseButton
                                    type="secondary"
                                    label={t('papCase.downloadPdf')}
                                    icon={<DownloadOutlined />}
                                    onClick={handleDownloadPapForm}
                                  />
                                )}
                              {!isViewOnly && (
                                <BaseButton
                                  type="secondary"
                                  label={t('papCase.edit')}
                                  icon={<EditOutlined />}
                                  onClick={handleEditMedication}
                                  disabled={!isEditableMedication}
                                />
                              )}
                            </div>
                          </div>
                        )}

                        {isPapFormPdfLoading ? (
                          <div className={s['pap-form-empty']}>
                            <BaseLoader loading />
                          </div>
                        ) : (
                          papFormContent
                        )}
                      </div>
                    ),
                  },
                  {
                    key: 'additionalInfo',
                    label: t('papCase.tabAdditionalInfo'),
                    children: (
                      <div className={s['tab-pane']}>
                        <div className={s['additional-header']}>
                          <p className={s['letter-title']}>
                            {t('papCase.additionalContent')}
                          </p>
                          <p className={s['letter-helper']}>
                            {t('papCase.additionalContentHelper')}
                          </p>
                        </div>

                        <div className={s['additional-section']}>
                          <div className={s['additional-section-header']}>
                            <p className={s['additional-section-title']}>
                              {t('papCase.nurseNote')}
                            </p>
                            {!isViewOnly && !isEditingNurseNote && (
                              <BaseButton
                                type="secondary"
                                label={t('papCase.edit')}
                                icon={<EditOutlined />}
                                onClick={handleEditNurseNote}
                                disabled={!isEditableNurseNote}
                              />
                            )}
                            {isEditingNurseNote && (
                              <div className={s['letter-actions']}>
                                <BaseButton
                                  type="secondary"
                                  label={t('controls.cancel')}
                                  onClick={handleCancelNurseNote}
                                />
                                <BaseButton
                                  label={t('papCase.save')}
                                  onClick={handleSaveNurseNote}
                                  loading={isUpdatingNurseNote}
                                  disabled={isModalApiSaving}
                                />
                              </div>
                            )}
                          </div>

                          {isEditingNurseNote && (
                            <Input.TextArea
                              className={s['additional-note-input']}
                              value={draftNurseNote}
                              placeholder={t('papCase.nurseNotePlaceholder')}
                              onChange={(event) =>
                                setDraftNurseNote(event.target.value)
                              }
                            />
                          )}
                          {!isEditingNurseNote &&
                            isViewOnly &&
                            currentCase?.note && (
                              <p className={s['additional-note-text']}>
                                {currentCase.note}
                              </p>
                            )}
                          {!isEditingNurseNote &&
                            isViewOnly &&
                            !currentCase?.note && (
                              <p className={s['additional-ro-empty']}>
                                {t('papCase.noNote')}
                              </p>
                            )}
                          {!isEditingNurseNote && !isViewOnly && (
                            <p className={s['additional-note-view']}>
                              {currentCase?.note ||
                                t('papCase.nurseNotePlaceholder')}
                            </p>
                          )}
                        </div>

                        <div className={s['additional-section']}>
                          <div className={s['additional-section-header']}>
                            <p className={s['additional-section-title']}>
                              {t('papCase.attachments')}
                            </p>
                            {!isViewOnly && (
                              <BaseButton
                                type="secondary"
                                label={t('papCase.clickToUpload')}
                                onClick={handleOpenUploadModal}
                                icon={<UploadOutlined />}
                                disabled={
                                  attachments.length >= MAX_ATTACHMENTS ||
                                  !isEditableAttachments
                                }
                              />
                            )}
                          </div>

                          {!attachments.length && isViewOnly && (
                            <p className={s['additional-ro-empty']}>
                              {t('papCase.noAttachment')}
                            </p>
                          )}
                          {attachments.length ? (
                            <div className={s['additional-list']}>
                              {attachments.map((attachment) => (
                                <div
                                  key={attachment.guid}
                                  className={s['additional-item']}
                                >
                                  <div className={s['additional-item-content']}>
                                    <OutlinedPaperClipBG />
                                    <p className={s['additional-item-title']}>
                                      {attachment.fileName}
                                      <span
                                        className={s['additional-item-meta']}
                                      >
                                        {t('papCase.attachmentMeta', {
                                          createdAt: DateFormatter({
                                            date: attachment.createdAt,
                                            format: DateFormats['MM/dd/yyyy'],
                                          }),
                                          createdBy: attachment.createdBy,
                                        })}
                                      </span>
                                    </p>
                                  </div>

                                  <div className={s['additional-item-actions']}>
                                    {isReviewerView && (
                                      <Button
                                        type="text"
                                        className={
                                          s['additional-remove-button']
                                        }
                                        onClick={() =>
                                          handleDownloadAttachment(attachment)
                                        }
                                        icon={<DownloadOutlined />}
                                      />
                                    )}
                                    {!isReviewerView && !isViewOnly && (
                                      <Button
                                        type="text"
                                        className={
                                          s['additional-remove-button']
                                        }
                                        onClick={() =>
                                          handleDeleteAttachment(
                                            attachment.guid,
                                          )
                                        }
                                        icon={<DeleteOutlined />}
                                        loading={isDeletingPapCaseAttachment}
                                        disabled={
                                          !isEditableAttachments ||
                                          isDeletingPapCaseAttachment
                                        }
                                      />
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            !isViewOnly && (
                              <div className={s['additional-empty']}>
                                <span className={s['additional-empty-icon']}>
                                  <PaperClipOutlined />
                                </span>
                                <p className={s['additional-empty-text']}>
                                  {t('papCase.noAttachmentsUploaded')}
                                </p>
                              </div>
                            )
                          )}
                        </div>

                        <div className={s['additional-section']}>
                          <div className={s['additional-section-header']}>
                            <p className={s['additional-section-title']}>
                              {t('papCase.externalResources')}
                            </p>
                            {!isViewOnly && !isAddingExternalLink && (
                              <BaseButton
                                type="secondary"
                                label={t('papCase.addExternalLink')}
                                onClick={() => setIsAddingExternalLink(true)}
                                loading={
                                  isLoadingExternalLinks ||
                                  isFetchingExternalLinks
                                }
                                disabled={
                                  isLoadingExternalLinks ||
                                  isFetchingExternalLinks ||
                                  externalLinks?.length >= MAX_EXTERNAL_LINKS ||
                                  !isEditableLinks
                                }
                                icon={<PlusOutlined />}
                              />
                            )}
                          </div>

                          {isAddingExternalLink && (
                            <div className={s['resource-add-form']}>
                              <div className={s['resource-add-inputs']}>
                                <div className={s['resource-add-input']}>
                                  <p>{t('papCase.title')}</p>
                                  <Input
                                    value={draftExternalResourceTitle}
                                    placeholder={t(
                                      'papCase.resourceTitlePlaceholder',
                                    )}
                                    onChange={(event) =>
                                      setDraftExternalResourceTitle(
                                        event.target.value,
                                      )
                                    }
                                  />
                                </div>
                                <div className={s['resource-add-input']}>
                                  <p>{t('papCase.url')}</p>
                                  <Input
                                    value={draftExternalResourceUrl}
                                    placeholder={t(
                                      'papCase.resourceUrlPlaceholder',
                                    )}
                                    onChange={(event) =>
                                      setDraftExternalResourceUrl(
                                        event.target.value,
                                      )
                                    }
                                  />
                                </div>
                              </div>
                              <div className={s['resource-add-input']}>
                                <p>{t('papCase.descriptionOptional')}</p>
                                <Input
                                  value={draftExternalResourceDescription}
                                  placeholder={t(
                                    'papCase.resourceDescriptionPlaceholder',
                                  )}
                                  onChange={(event) =>
                                    setDraftExternalResourceDescription(
                                      event.target.value,
                                    )
                                  }
                                />
                              </div>
                              <div className={s['resource-add-actions']}>
                                <BaseButton
                                  type="secondary"
                                  label={t('controls.cancel')}
                                  onClick={() => {
                                    setIsAddingExternalLink(false);
                                    setDraftExternalResourceTitle('');
                                    setDraftExternalResourceUrl('');
                                    setDraftExternalResourceDescription('');
                                  }}
                                />
                                <BaseButton
                                  label={t('papCase.addLink')}
                                  onClick={handleAddExternalLink}
                                  loading={isCreatingExternalLink}
                                  disabled={
                                    !externalLinkTitle ||
                                    !isExternalLinkUrlValid ||
                                    isLoadingExternalLinks ||
                                    isFetchingExternalLinks ||
                                    isCreatingExternalLink ||
                                    isDeletingExternalLink
                                  }
                                />
                              </div>
                            </div>
                          )}

                          {externalResourcesContent}
                        </div>
                      </div>
                    ),
                  },
                ]}
              />
            </div>

            <div className={s['case-panel']}>
              {currentCase?.latestReview?.note && (
                <div
                  className={`${s['case-panel-section']} ${s['review-note']}`}
                >
                  <div>
                    <p className={s.title}>{t('papCase.reviewerNote')}</p>
                    <p className={s['sub-info']}>
                      <span>{`${
                        currentCase.latestReview.createdBy.title || ''
                      } ${currentCase.latestReview.createdBy.firstName} ${
                        currentCase.latestReview.createdBy.lastName
                      }`}</span>
                      <span>
                        {DateFormatter({
                          date: currentCase.latestReview.createdAt,
                          format: DateFormats['MM/dd/yyyy'],
                        })}
                      </span>
                    </p>
                    <p
                      className={`${s.note}${
                        isLongReview && !showFullNote
                          ? ` ${s['note-truncated']}`
                          : ''
                      }`}
                    >
                      {currentCase.latestReview.note}
                    </p>
                    {isLongReview &&
                      (showFullNote ? (
                        <span
                          className={s['show-more']}
                          onClick={() => setShowFullNote(false)}
                        >
                          {t('papCase.showLess')}

                          <UpOutlined />
                        </span>
                      ) : (
                        <span
                          className={s['show-less']}
                          onClick={() => setShowFullNote(true)}
                        >
                          {t('papCase.showMore')}
                          <DownOutlined />
                        </span>
                      ))}
                  </div>
                </div>
              )}

              <div className={s['case-panel-section']}>
                <div className={s['case-title-row']}>
                  <p className={s['case-title']}>{t('papCase.caseDetail')}</p>
                  {!isViewOnly && !isEditingCaseDetail && (
                    <BaseButton
                      type="secondary"
                      icon={<EditOutlined />}
                      onClick={() => setIsEditingCaseDetail(true)}
                    />
                  )}
                </div>

                {!isViewOnly && isEditingCaseDetail && (
                  <div className={s['case-actions']}>
                    <BaseButton
                      type="secondary"
                      label={t('controls.cancel')}
                      onClick={onCancelCaseDetail}
                      disabled={isUpdatingCase}
                    />
                    <BaseButton
                      label={t('papCase.save')}
                      onClick={handleUpdateCaseDetail}
                      loading={isUpdatingCase}
                      disabled={isModalApiSaving}
                    />
                  </div>
                )}

                <div className={s['case-item']}>
                  <p className={s['case-label']}>{t('papCase.caseName')}</p>
                  {isEditingCaseDetail ? (
                    <Input
                      value={draftCaseName}
                      onChange={(e) => setDraftCaseName(e.target.value)}
                    />
                  ) : (
                    <p className={s['case-value']}>{currentCase?.name}</p>
                  )}
                </div>

                <div className={s['case-item']}>
                  <p className={s['case-label']}>{t('labels.status')}</p>
                  <div>
                    <Tag className={s[`status-${currentCase?.status}`]}>
                      {getStatusLabel(currentCase?.status ?? 'draft', t)}
                    </Tag>
                  </div>
                </div>

                <div className={s['case-item']}>
                  <p className={s['case-label']}>{t('labels.patient')}</p>
                  <div className={s['case-patient-info']}>
                    <div className={s['case-patient-name-row']}>
                      {isReviewerView ? (
                        <>
                          <Tooltip title={patientName}>
                            <a
                              href={Routes.patientGuid(patientGuid)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={s['case-patient-name']}
                            >
                              {patientName}
                            </a>
                          </Tooltip>
                          <ExportOutlined className={s['case-patient-icon']} />
                        </>
                      ) : (
                        <Tooltip title={patientName}>
                          <p className={s['case-value']}>{patientName}</p>
                        </Tooltip>
                      )}
                    </div>
                    <p className={s['case-patient-dob']}>{patientDob}</p>
                  </div>
                </div>

                <div className={s['case-item']}>
                  <p className={s['case-label']}>
                    {t('papCase.papFormVersion')}
                  </p>
                  <p className={s['case-value']}>
                    {currentCase?.latestForm?.version != null
                      ? `v${currentCase.latestForm.version}`
                      : '-'}
                  </p>
                </div>

                <div className={s['case-item']}>
                  <p className={s['case-label']}>{t('papCase.specialist')}</p>
                  {isEditingCaseDetail ? (
                    <Select
                      value={draftSpecialist ? 'yes' : 'no'}
                      options={[
                        { label: t('controls.yes'), value: 'yes' },
                        { label: t('controls.no'), value: 'no' },
                      ]}
                      onChange={(value) => setDraftSpecialist(value === 'yes')}
                    />
                  ) : (
                    <p className={s['case-value']}>
                      {currentCase?.isSpecialist
                        ? t('controls.yes')
                        : t('controls.no')}
                    </p>
                  )}
                </div>

                {isReviewerView && (
                  <div className={s['case-item']}>
                    <p className={s['case-label']}>{t('labels.lastUpdate')}</p>
                    <p className={s['case-value']}>
                      {currentCase?.updatedAt
                        ? DateFormatter({
                            date: currentCase.updatedAt,
                            format: DateFormats['MM/dd/yyyy, hh:mm a'],
                          })
                        : '-'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={s['controller-row']}>
            <PapCaseControllers
              caseStatus={currentCase?.status as PapCaseStatus}
              handleReadyForReview={handleStatusTransaction}
              isDisabled={
                currentCase?.status === 'draft' &&
                (!currentCase.medicationGuid || !currentCase.content)
              }
              handleOpenCaseReview={setOpeningCaseStatus}
              isReviewerView={isReviewerView}
            />
          </div>
        </div>

        {isModalLoading && (
          <div className={s['modal-loading-overlay']}>
            <div className={s['modal-loading-backdrop']} />
            <div className={s['modal-loading-spinner']}>
              <BaseLoader loading />
            </div>
          </div>
        )}
      </div>

      <Modal
        open={editingWarning.open}
        title={null}
        closable={false}
        footer={null}
        centered
        zIndex={PAP_CASE_STACKED_MODAL_Z_INDEX}
        width={416}
        destroyOnClose
        wrapClassName={s['editing-warning-modal']}
      >
        <div className={s['dialog-body']}>
          <ExclamationCircleFilled className={s['dialog-icon']} />
          <div className={s['dialog-content']}>
            <p className={s['dialog-title']}>
              {t('papCase.finishEditingFirst')}
            </p>
            <p className={s['dialog-description']}>{editingWarning.message}</p>
          </div>
        </div>
        <div className={s['dialog-footer']}>
          <Button
            type="primary"
            onClick={() => setEditingWarning({ open: false, message: '' })}
          >
            {t('papCase.ok')}
          </Button>
        </div>
      </Modal>

      <Modal
        open={discardConfirmOpen}
        title={null}
        closable={false}
        footer={null}
        centered
        zIndex={PAP_CASE_STACKED_MODAL_Z_INDEX}
        width={416}
        destroyOnClose
        wrapClassName={s['discard-confirm-modal']}
      >
        <div className={s['dialog-body']}>
          <ExclamationCircleFilled className={s['dialog-icon']} />
          <div className={s['dialog-content']}>
            <p className={s['dialog-title']}>
              {t('papCase.discardUnsavedChangesTitle')}
            </p>
            <p className={s['dialog-description']}>
              {t('papCase.discardUnsavedChangesContent')}
            </p>
          </div>
        </div>
        <div className={s['dialog-footer']}>
          <Button onClick={() => setDiscardConfirmOpen(false)}>
            {t('papCase.keepEditing')}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              setDiscardConfirmOpen(false);
              onClose();
            }}
          >
            {t('papCase.discard')}
          </Button>
        </div>
      </Modal>

      <CaseReviewModal
        isOpen={openingCaseStatus !== null}
        openingCaseStatus={openingCaseStatus as PapCaseStatus}
        onClose={() => setOpeningCaseStatus(null)}
        onConfirm={(nextState: PapCaseStatus, reviewNote?: string) => {
          handleStatusTransaction(
            currentCase?.status as PapCaseStatus,
            nextState,
            reviewNote,
          );
        }}
        isSavingData={isUpdatingStatusWithReview || isUpdatingStatus}
        zIndex={PAP_CASE_STACKED_MODAL_Z_INDEX}
      />

      <Modal
        open={isUploadModalOpen}
        title={t('papCase.uploadAttachment')}
        onCancel={() => {
          setIsUploadModalOpen(false);
          setSelectedUploadFile(null);
        }}
        footer={null}
        destroyOnClose
        zIndex={PAP_CASE_STACKED_MODAL_Z_INDEX}
      >
        <div className={s['upload-modal-body']}>
          <p className={s['upload-modal-helper']}>
            {t('papCase.uploadAttachmentHelper')}
          </p>
          <input
            ref={hiddenUploadInputRef}
            type="file"
            className={s['hidden-upload-input']}
            onChange={(event) =>
              setSelectedUploadFile(event.target.files?.[0] || null)
            }
          />
          <div className={s['upload-modal-select']}>
            <BaseButton
              type="secondary"
              label={t('papCase.chooseFile')}
              onClick={() => hiddenUploadInputRef.current?.click()}
            />
            <span className={s['upload-modal-file-name']}>
              {selectedUploadFile?.name || t('papCase.noFileSelected')}
            </span>
          </div>
          <div className={s['upload-modal-actions']}>
            <BaseButton
              type="secondary"
              label={t('controls.cancel')}
              onClick={() => {
                setIsUploadModalOpen(false);
                setSelectedUploadFile(null);
              }}
            />
            <BaseButton
              label={t('papCase.upload')}
              onClick={handleUploadAttachment}
              disabled={!selectedUploadFile}
              loading={isSavingPapCaseAttachment}
            />
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default PapCaseDetailsModal;
